import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowLeft, CheckCircle2, AlertCircle, ExternalLink } from "lucide-react";

const STATUS = {
  draft: { label: "Draft", cls: "bg-muted text-foreground/70" },
  pending_payment: { label: "Awaiting payment", cls: "bg-accent/15 text-accent-dark" },
  active: { label: "Active", cls: "bg-success/15 text-success" },
  ended: { label: "Ended", cls: "bg-muted text-muted-foreground" },
  removed: { label: "Removed", cls: "bg-destructive/10 text-destructive" },
};

export default function AdDetailPage() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const [ad, setAd] = useState(null);
  const [pollMsg, setPollMsg] = useState(null);
  const paid = params.get("paid") === "1";
  const canceled = params.get("canceled") === "1";
  const sessionId = params.get("session_id");

  const refresh = async () => {
    const r = await api.get(`/ads/${id}`);
    setAd(r.data);
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (!paid || !sessionId) return;
    let attempts = 0;
    const tick = async () => {
      attempts += 1;
      try {
        const r = await api.get(`/ads/checkout/status/${sessionId}`);
        if (r.data.payment_status === "paid") {
          setPollMsg("paid");
          await refresh();
          return;
        }
        if (r.data.status === "expired") {
          setPollMsg("expired");
          return;
        }
      } catch {}
      if (attempts < 6) setTimeout(tick, 2000);
    };
    tick();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paid, sessionId]);

  if (!ad) return <div className="container-page py-16 text-muted-foreground">Loading…</div>;
  const s = STATUS[ad.status] || { label: ad.status, cls: "bg-muted" };

  return (
    <div className="container-page py-12" data-testid="ad-detail-page">
      <Link to="/dashboard/ads" className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:text-accent">
        <ArrowLeft className="h-4 w-4" /> Back to my ads
      </Link>

      {paid && (
        <div className="mt-6 flex items-center gap-3 rounded-lg bg-success/10 p-4 text-success" data-testid="paid-banner">
          <CheckCircle2 className="h-5 w-5" />
          <p className="font-semibold">{pollMsg === "paid" || ad.status === "active" ? "Payment received — your ad is live!" : "Confirming payment…"}</p>
        </div>
      )}
      {canceled && (
        <div className="mt-6 flex items-center gap-3 rounded-lg bg-muted p-4 text-foreground/80" data-testid="canceled-banner">
          <AlertCircle className="h-5 w-5" />
          <p>Payment canceled — you can retry from your dashboard.</p>
        </div>
      )}

      <div className="card-surface mt-8 grid gap-6 p-6 lg:grid-cols-[1fr_1.4fr]">
        <div className="aspect-[16/10] overflow-hidden rounded-lg bg-muted">
          {ad.image_url && (
            <img
              src={ad.image_url.startsWith("http") ? ad.image_url : `${process.env.REACT_APP_BACKEND_URL}${ad.image_url}`}
              alt={ad.headline}
              className="h-full w-full object-cover"
            />
          )}
        </div>
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <span className={`rounded-full px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${s.cls}`}>{s.label}</span>
            <span className="chip">{ad.placement}</span>
            <span className="chip">{ad.budget_tier}</span>
          </div>
          <h1 className="mt-3 font-display text-3xl font-bold text-primary">{ad.headline}</h1>
          <a href={ad.link_url} target="_blank" rel="noreferrer" className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:text-accent" data-testid="ad-link">
            {ad.link_url} <ExternalLink className="h-3.5 w-3.5" />
          </a>
          <dl className="mt-6 grid gap-3 text-sm">
            <div className="flex justify-between border-b border-border py-2"><dt className="text-muted-foreground">Schedule</dt><dd>{new Date(ad.start_at).toLocaleDateString()} → {new Date(ad.end_at).toLocaleDateString()}</dd></div>
            <div className="flex justify-between border-b border-border py-2"><dt className="text-muted-foreground">Budget</dt><dd>${(ad.budget_cents / 100).toFixed(2)} USD</dd></div>
            <div className="flex justify-between border-b border-border py-2"><dt className="text-muted-foreground">Impressions</dt><dd>{ad.impressions || 0}</dd></div>
            <div className="flex justify-between py-2"><dt className="text-muted-foreground">Clicks</dt><dd>{ad.clicks || 0}</dd></div>
            {ad.stripe_payment_intent_id && (
              <div className="flex justify-between border-t border-border pt-2 text-xs">
                <dt className="text-muted-foreground">Payment</dt>
                <dd className="font-mono">{ad.stripe_payment_intent_id}</dd>
              </div>
            )}
          </dl>
        </div>
      </div>
    </div>
  );
}
